/* eslint-disable react-hooks/rules-of-hooks */
import React, { useMemo } from 'react';

import { useRecordContext, SelectField, TextField } from 'react-admin';

import { get, isArray } from 'lodash';

import {
  Box, Card, CardContent, Divider, Grid, Typography,
} from '@mui/material';

import Answer from '../Answer';

import languages, { Languages } from '../../../../constants/languages';
import { countryChoices } from '../../../../constants/countries';
import { identifierTypeChoices } from '../../../../constants/identifierTypes';
import applyChoices from '../../applyChoices';
import { NationalIdentifierParameters } from '../../questionParameters';

const TITLE = {
  [languages.SV]: 'Nationell identifierare',
  [languages.EN]: 'National identifier',
  [languages.DE]: 'Nationale Identifikationsnummer',
};

const COUNTRY_FIELD_LABEL = {
  [languages.SV]: 'Land',
  [languages.EN]: 'Country',
  [languages.DE]: 'Land',
};

const IDENTIFIER_FIELD_LABEL = {
  [languages.SV]: 'Identifierare',
  [languages.EN]: 'Identifier',
  [languages.DE]: 'Identifikationsnummer',
};

const extendedIdentifierTypeChoices = (language: Languages) => [
  ...identifierTypeChoices(language),
  {
    id: 'dob',
    name: language === languages.SV ? 'Födelsedatum' : 'Date of Birth',
  },
];

const ValueField = ({
  language,
  extraParameters,
}: {
  language?: Languages;
  extraParameters?: NationalIdentifierParameters;
}) => {
  const record = useRecordContext();

  if (!language) return null;

  const value = get(record, 'value') as {
    country: string;
    identifier?: string;
    type?: string;
    fallbackToDob?: boolean;
  }[] | undefined;
  const sx = get(record, 'sx', {});

  if (!isArray(value)) return null;

  const fields = extraParameters?.fields;

  return (
    <Card variant="outlined" sx={{ m: 0 }}>
      <Grid size={12}>
        <Grid container>
          {value.map((item, i) => (
            <Grid
              key={item.country}
              size={12}
            >
              <CardContent sx={{ p: 4 }}>
                <Grid container>
                  <Grid
                    size={{
                      xs: 12,
                      md: 6,
                    }}
                  >
                    <Box display="flex" flexDirection="column">
                      <Typography variant="label">{fields?.countries?.label ?? COUNTRY_FIELD_LABEL[language]}</Typography>
                      <SelectField
                        key={item.country}
                        record={{ item }}
                        source="item.country"
                        choices={countryChoices(language)}
                        fontWeight="bold"
                        sx={sx}
                      />
                    </Box>
                  </Grid>
                  <Grid
                    size={{
                      xs: 12,
                      md: 6,
                    }}
                  >
                    <Box display="flex" flexDirection="column">
                      <Typography variant="label">{fields?.identifier?.label ?? IDENTIFIER_FIELD_LABEL[language]}</Typography>
                      <Box display="flex" gap={2}>
                        <SelectField
                          record={item}
                          choices={applyChoices(
                            extendedIdentifierTypeChoices(language),
                            fields?.type?.options,
                            fields?.type?.choiceLabels,
                          )}
                          source="type"
                        />
                        <Typography>-</Typography>
                        <TextField
                          record={item}
                          source="identifier"
                        />
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
              {(i + 1) !== value.length && <Divider />}
            </Grid>
          ))}
        </Grid>
      </Grid>
    </Card>
  );
};

const formatExtendedNationalIdentifier = (dobValue: string | undefined) => (value: {
  country: string;
  identifier?: string;
  type?: string;
  fallbackToDob?: boolean;
}[] | undefined) => {
  if (!isArray(value)) return value;

  return value.map((item) => ({
    ...item,
    ...(item.fallbackToDob ? { type: 'dob', identifier: dobValue } : {}),
  }));
};

const NationalIdentifier = ({
  source,
  extraParameters,
}: {
  source?: string;
  extraParameters?: NationalIdentifierParameters;
}) => {
  const record = useRecordContext();

  const language = record?.language as Languages;

  const dateOfBirth = get(record, 'dateOfBirth') as string | undefined;
  const formattedDateOfBirth = dateOfBirth ? new Date(dateOfBirth).toLocaleDateString() : undefined;

  return (
    <Answer
      label={extraParameters?.sections?.nationalIdentifier?.label ?? TITLE[language]}
      source={source ?? 'nationalIdentifier'}
      datapoint={null}
      identificationInfo={undefined}
      isComplexType
      formatter={formatExtendedNationalIdentifier(formattedDateOfBirth)}
    >
      <ValueField language={language} extraParameters={extraParameters} />
    </Answer>
  );
};

NationalIdentifier.Nested = ({
  source,
  extraParameters,
}: {
  source?: string;
  extraParameters?: NationalIdentifierParameters;
}) => {
  const record = useRecordContext();

  const language = record?.language as Languages ?? languages.EN;

  const dateOfBirth = get(record, 'dateOfBirth') as string | undefined;
  const formattedDateOfBirth = dateOfBirth ? new Date(dateOfBirth).toLocaleDateString() : undefined;

  const countries = useMemo(() => {
    const nationalIdentifier = get((record), `${source ?? 'nationalIdentifier'}`) as {
      country: string;
      identifier?: string;
      type?: string;
      fallbackToDob?: boolean;
    }[] | undefined;
    return formatExtendedNationalIdentifier(formattedDateOfBirth)(nationalIdentifier);
  }, [source, record, formattedDateOfBirth]);

  return countries?.map((item) => (
    <Grid
      key={item.country}
      size={{
        xs: 12,
        md: 3,
      }}
    >
      <Box display="flex" flexDirection="column">
        <Box gap={1} display="flex">
          <Typography variant="label">National identifier for</Typography>
          <SelectField
            record={item}
            source="country"
            variant="label"
            choices={countryChoices(language)}
          />
        </Box>
        <Box display="flex" gap={2}>
          <SelectField
            record={item}
            choices={applyChoices(
              extendedIdentifierTypeChoices(language),
              extraParameters?.fields?.type?.options,
              extraParameters?.fields?.type?.choiceLabels,
            )}
            source="type"
          />
          <Typography>-</Typography>
          <TextField
            record={item}
            source="identifier"
          />
        </Box>
      </Box>
    </Grid>
  ));
};

export default NationalIdentifier;
